import type { ReportGranularity } from '@zeus/index'
import type { ReportForm } from './report-form'
import type { ReportColumn, ReportTemplate } from '~/gql/reports/reports'
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GQL_CREATE_REPORT_TEMPLATE, GQL_REPORT_COLUMNS, GQL_UPDATE_REPORT_TEMPLATE } from '~/gql/reports/reports'
import { emptyForm, formErrors, formFromTemplate, formToInput, withGranularity } from './report-form'

/**
 * L'édition d'un modèle de rapport (#163) : un nouveau quand `template` est
 * nul. Les colonnes proposées viennent du catalogue de l'API pour la
 * granularité choisie, seule source de ce qu'un modèle peut porter.
 */
export function useReportTemplateForm(template: ReportTemplate | null, onSaved: () => void) {
  const { t } = useTranslation()
  const client = useApolloClient()
  const [initial] = useState(() => (template ? formFromTemplate(template) : emptyForm()))
  const [form, setForm] = useState<ReportForm>(initial)
  const [saving, setSaving] = useState(false)
  const { data } = useQuery(GQL_REPORT_COLUMNS, { variables: { granularity: form.granularity } })
  const columns: ReportColumn[] = data?.reportColumns ?? []
  const [createTemplate] = useMutation(GQL_CREATE_REPORT_TEMPLATE)
  const [updateTemplate] = useMutation(GQL_UPDATE_REPORT_TEMPLATE)
  const errors = formErrors(form)

  /** Les colonnes que la nouvelle granularité n'autorise pas sont retirées du modèle. */
  const changeGranularity = async (granularity: ReportGranularity) => {
    try {
      const { data: next } = await client.query({ query: GQL_REPORT_COLUMNS, variables: { granularity } })
      const available = new Set((next?.reportColumns ?? []).map(column => column.key))
      setForm(current => withGranularity(current, granularity, available))
    } catch {
      toast.error(t('reports.error'))
    }
  }

  /** `true` une fois enregistré : l'écran revient alors à la liste. */
  const save = async (): Promise<boolean> => {
    if (Object.keys(errors).length > 0 || saving) return false
    setSaving(true)
    try {
      const input = formToInput(form)
      if (template) await updateTemplate({ variables: { id: template.id, input } })
      else await createTemplate({ variables: { input } })
      toast.success(t(template ? 'reports.saved' : 'reports.created'))
      onSaved()
      return true
    } catch {
      toast.error(t('reports.error'))
      return false
    } finally {
      setSaving(false)
    }
  }

  return {
    form,
    setForm,
    columns,
    errors,
    saving,
    dirty: JSON.stringify(form) !== JSON.stringify(initial),
    changeGranularity,
    save,
  }
}
